import * as React from "react";
import { Loader2, Waypoints } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsBelowLg } from "@/hooks/use-mobile";

import type { WorkspaceNode } from "../../../types/workspace";
import { WorkspaceFocusFilterBar } from "./WorkspaceFocusFilterBar";
import type {
  WorkspaceFocusEnrichment,
  WorkspaceFocusFilter,
} from "./workspaceFocusFilter";
import {
  WORKSPACE_LAYOUT_MODE_OPTIONS,
  type WorkspaceLayoutMode,
} from "./workspaceLayoutMode";
import {
  FieldSelectFit,
  type TradeFilterSelectOption,
} from "@/components/trade-filter-selects";

export type WorkspaceToolbarProps = {
  workspaceName: string;
  /** Account canvas vs Strategy federated canvas. */
  viewMode?: "account" | "strategy";
  /** Account chips for Strategy workspace. */
  contributingAccountLabels?: string[];
  variant?: "default" | "overlay";
  onOrganizeLayout?: () => void | Promise<void>;
  organizeLayoutDisabled?: boolean;
  organizeLayoutPending?: boolean;
  /** Strategy only — Parallel vs Sequential. */
  layoutMode?: WorkspaceLayoutMode;
  onLayoutModeChange?: (mode: WorkspaceLayoutMode) => void;
  /** Canvas focus filter — dims non-matching nodes. */
  focusFilter?: WorkspaceFocusFilter;
  onFocusFilterChange?: (next: WorkspaceFocusFilter) => void;
  focusFilterNodes?: WorkspaceNode[];
  focusEnrichment?: WorkspaceFocusEnrichment;
};

export function WorkspaceToolbar({
  workspaceName,
  viewMode = "account",
  contributingAccountLabels = [],
  variant = "default",
  onOrganizeLayout,
  organizeLayoutDisabled = false,
  organizeLayoutPending = false,
  layoutMode,
  onLayoutModeChange,
  focusFilter,
  onFocusFilterChange,
  focusFilterNodes,
  focusEnrichment,
}: WorkspaceToolbarProps): React.ReactElement {
  const isBelowLg = useIsBelowLg();
  const isOverlay = variant === "overlay";
  const isStrategy = viewMode === "strategy";
  const titlePrefix = isStrategy ? "Strategy workspace" : "Workspace";
  const showFocusFilter =
    focusFilter != null &&
    onFocusFilterChange != null &&
    focusFilterNodes != null &&
    focusEnrichment != null;
  const showLayoutMode =
    isStrategy && layoutMode != null && onLayoutModeChange != null;
  const layoutModeOptions = React.useMemo<TradeFilterSelectOption[]>(
    () =>
      WORKSPACE_LAYOUT_MODE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    []
  );

  const toolbarTitle = (
    <h1
      data-workspace-toolbar-title=""
      className={cn(
        "min-w-0 truncate font-[family-name:var(--font-primary)] text-base font-medium",
        isBelowLg ? "flex-1" : "shrink-0 md:max-w-[12rem] lg:max-w-[16rem]"
      )}
      title={`Workspace: ${workspaceName}`}
    >
      <span className="text-[color:var(--brand-tropical-surf)]">Workspace</span>
      <span className="text-[#6b7280] dark:text-white">: {workspaceName}</span>
    </h1>
  );

  const layoutModeControl = showLayoutMode ? (
    <div
      data-workspace-layout-mode=""
      className="flex shrink-0 items-center gap-1.5"
    >
      <span className="hidden font-[family-name:var(--font-primary)] text-[10px] text-muted-foreground sm:inline">
        Layout
      </span>
      <FieldSelectFit
        filterChrome="reports"
        value={layoutMode}
        onValueChange={(value) => {
          if (value === "parallel" || value === "sequential") {
            onLayoutModeChange(value);
          }
        }}
        options={layoutModeOptions}
      />
    </div>
  ) : null;

  const organizeControl = onOrganizeLayout ? (
    <Button
      type="button"
      variant="outline-brand"
      size={isBelowLg ? "icon" : "sm"}
      data-font="primary"
      data-workspace-organize-layout=""
      className={cn(isBelowLg ? "h-8 w-8 shrink-0" : "shrink-0")}
      aria-label="Auto Layout"
      disabled={organizeLayoutDisabled || organizeLayoutPending}
      onClick={() => {
        void onOrganizeLayout();
      }}
    >
      {organizeLayoutPending ? (
        <Loader2
          data-icon={isBelowLg ? undefined : "inline-start"}
          className="size-4 shrink-0 animate-spin"
          aria-hidden
        />
      ) : (
        <Waypoints
          data-icon={isBelowLg ? undefined : "inline-start"}
          className="size-4 shrink-0"
          aria-hidden
        />
      )}
      {isBelowLg ? null : "Auto Layout"}
    </Button>
  ) : null;

  const focusFilterControl = showFocusFilter ? (
    <WorkspaceFocusFilterBar
      filter={focusFilter}
      onChange={onFocusFilterChange}
      nodes={focusFilterNodes}
      enrichment={focusEnrichment}
      className={isBelowLg ? "w-full flex-none" : undefined}
    />
  ) : null;

  return (
    <div
      data-workspace-toolbar=""
      data-workspace-toolbar-variant={variant}
      data-workspace-view-mode={viewMode}
      data-workspace-toolbar-mobile={isBelowLg ? "" : undefined}
      data-reports-action-bar={showFocusFilter ? "" : undefined}
      className={cn(
        "flex gap-3 rounded-[6px] border bg-card px-3 py-2 shadow-none",
        isBelowLg ? "flex-col" : "flex-wrap items-center",
        isOverlay && "backdrop-blur supports-[backdrop-filter]:bg-card/90"
      )}
    >
      {isBelowLg ? (
        <>
          <div className="flex min-w-0 items-center gap-3">
            {toolbarTitle}
            {layoutModeControl}
            {organizeControl}
          </div>
          {focusFilterControl}
        </>
      ) : (
        <>
          {toolbarTitle}
          {focusFilterControl ?? <div className="min-w-0 flex-1" />}
          {layoutModeControl}
          {organizeControl}
        </>
      )}
    </div>
  );
}
